import { useRef, type RefObject, type UIEvent } from 'react';
import { useListBox } from 'react-aria';
import type { AriaListBoxOptions } from '@react-aria/listbox';
import type { ListState } from 'react-stately';
import ListBoxOption from './ListBoxOption';
import s from './ListBox.module.scss';

/* For detailed prop types see react-aria */
interface ListBoxProps<T> extends AriaListBoxOptions<T> {
  state: ListState<T>;
  listBoxRef?: RefObject<HTMLUListElement | null>;
  handleLoadMore: () => void;
}

/** ListBox based on react-aria — https://react-spectrum.adobe.com/react-aria/useComboBox.html */
function ListBox<T>({
  listBoxRef: listBoxRefProp,
  state,
  handleLoadMore,
  ...listBoxAriaProps
}: ListBoxProps<T>) {
  const ref = useRef<HTMLUListElement>(null);
  const listBoxRef = listBoxRefProp ?? ref;
  const { listBoxProps } = useListBox(listBoxAriaProps, state, listBoxRef);

  const handleScroll = (e: UIEvent<HTMLUListElement>) => {
    const scrollOffset =
      e.currentTarget.scrollHeight - e.currentTarget.clientHeight * 2;
    if (e.currentTarget.scrollTop > scrollOffset) {
      handleLoadMore();
    }
  };

  return (
    <ul
      /* oxlint-disable-next-line react/jsx-props-no-spreading */
      {...listBoxProps}
      ref={listBoxRef}
      className={s.listBox}
      onScroll={handleScroll}
    >
      {Array.from(state.collection).map((item) => (
        <ListBoxOption key={item.key} item={item} state={state} />
      ))}
    </ul>
  );
}

export default ListBox;
